import { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Signature, TextT, CalendarBlank, FloppyDisk, Trash, Info } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { useAuth, formatApiErrorDetail } from '@/auth/AuthContext';
import { renderFormSheet, PAGE_CLASS } from '@/components/verify/formSheet';
import VerificationOverlay, { annotationStyle, signatureUrl } from '@/components/verify/VerificationOverlay';
import { STAGE_ORDER, eligibleStage, STAGE_ACTION, STAGE_BADGE, computeStage, formPaperMm } from '@/lib/workflow';
import { formatDate } from '@/utils/datetime';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
const uid = () => Math.random().toString(36).slice(2, 10);

function todayJP() {
  const d = new Date();
  return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日`;
}

// Which stage the user is acting on: by default their eligible pending stage, but
// a `?stage=` query lets the ORIGINAL signer (or admin) re-edit a completed stage
// — unless the whole submission is already finally approved.
function pickActiveStage(user, submission, stages, search) {
  const eligible = eligibleStage(user, submission, stages);
  const reqStage = new URLSearchParams(search || '').get('stage');
  if (reqStage && submission) {
    const verifs = submission.verifications || [];
    const owns = verifs.some(
      (v) => v.stage === reqStage && (user?.role === 'admin' || v.user_id === user?.id)
    );
    const fullyApproved = computeStage(submission, stages) === 'approved';
    if (owns && !fullyApproved) return reqStage;
  }
  return eligible;
}

function DraggableAnnotation({ a, containerRef, selected, onSelect, onChange, onDelete, signatureSrc }) {
  const beginDrag = (e) => {
    if (e.button !== undefined && e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    onSelect(a.id);
    const rect = containerRef.current.getBoundingClientRect();
    const sx = e.clientX, sy = e.clientY, ox = a.x, oy = a.y;
    const move = (ev) => {
      const dx = ((ev.clientX - sx) / rect.width) * 100;
      const dy = ((ev.clientY - sy) / rect.height) * 100;
      onChange({ ...a, x: clamp(ox + dx, 0, 100 - a.w), y: clamp(oy + dy, 0, 100 - a.h) });
    };
    const up = () => {
      document.removeEventListener('pointermove', move);
      document.removeEventListener('pointerup', up);
    };
    document.addEventListener('pointermove', move);
    document.addEventListener('pointerup', up);
  };

  const beginResize = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = containerRef.current.getBoundingClientRect();
    const sx = e.clientX, sy = e.clientY, ow = a.w, oh = a.h;
    const move = (ev) => {
      const dw = ((ev.clientX - sx) / rect.width) * 100;
      const dh = ((ev.clientY - sy) / rect.height) * 100;
      onChange({ ...a, w: clamp(ow + dw, 4, 100 - a.x), h: clamp(oh + dh, 3, 100 - a.y) });
    };
    const up = () => {
      document.removeEventListener('pointermove', move);
      document.removeEventListener('pointerup', up);
    };
    document.addEventListener('pointermove', move);
    document.addEventListener('pointerup', up);
  };

  return (
    <div
      style={{
        ...annotationStyle(a),
        pointerEvents: 'auto',
        cursor: 'move',
        outline: selected ? '2px solid #0052FF' : '1px dashed rgba(0,82,255,0.45)',
        background: selected ? 'rgba(0,82,255,0.04)' : 'transparent',
      }}
      onPointerDown={beginDrag}
      data-testid={`annotation-${a.id}`}
    >
      {a.type === 'signature' ? (
        <img src={signatureSrc} alt="署名" draggable={false}
             style={{ width: '100%', height: '100%', objectFit: 'contain', pointerEvents: 'none' }} />
      ) : (
        <div style={{
          width: '100%', height: '100%', display: 'flex', alignItems: 'center',
          fontSize: `${a.fontSize}cqw`, color: a.color, fontWeight: 600, lineHeight: 1.15,
          whiteSpace: 'pre-wrap', overflow: 'visible', pointerEvents: 'none',
        }}>
          {a.value || '（テキスト）'}
        </div>
      )}
      {selected && (
        <>
          <button
            type="button"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); onDelete(a.id); }}
            data-testid={`annotation-delete-${a.id}`}
            style={{
              position: 'absolute', top: -11, right: -11, width: 22, height: 22, borderRadius: '50%',
              background: '#ef4444', color: '#fff', fontSize: 14, lineHeight: '20px', border: '2px solid #fff',
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >×</button>
          <div
            onPointerDown={beginResize}
            data-testid={`annotation-resize-${a.id}`}
            style={{
              position: 'absolute', bottom: -7, right: -7, width: 16, height: 16, background: '#0052FF',
              borderRadius: 4, cursor: 'nwse-resize', border: '2px solid #fff',
            }}
          />
        </>
      )}
    </div>
  );
}

export default function VerifySubmission() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const containerRef = useRef(null);
  const [submission, setSubmission] = useState(null);
  const [stages, setStages] = useState(STAGE_ORDER);
  const [annotations, setAnnotations] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [hasSignature, setHasSignature] = useState(false);
  const [sigDefault, setSigDefault] = useState({ w: 7, h: 5 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [subRes, sigRes, cfgRes] = await Promise.all([
        axios.get(`${API}/forms/submissions/${id}`),
        axios.get(`${API}/signatures/me`),
        axios.get(`${API}/workflow-config`).catch(() => ({ data: { forms: [] } })),
      ]);
      setSubmission(subRes.data);
      setHasSignature(!!sigRes.data.has_signature);
      if (cfgRes.data.enabled === false) {
        toast.error('承認ワークフローは現在無効化されています');
        navigate(`/submission/${id}`);
        return;
      }
      const paper = formPaperMm(subRes.data.form_type);
      const mm = sigRes.data.default_mm || 15;
      setSigDefault({ w: +(mm / paper.w * 100).toFixed(2), h: +(mm / paper.h * 100).toFixed(2) });
      const cfg = (cfgRes.data.forms || []).find((f) => f.form_type === subRes.data.form_type);
      const formStages = cfg?.stages || STAGE_ORDER;
      setStages(formStages);
      const loadStage = pickActiveStage(user, subRes.data, formStages, location.search);
      if (loadStage) {
        const existing = (subRes.data.verifications || []).find((v) => v.stage === loadStage);
        setAnnotations(existing ? (existing.annotations || []) : []);
      }
    } catch (e) {
      toast.error('読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  }, [id, user, location.search, navigate]);

  useEffect(() => { load(); }, [load]);

  const update = (next) => setAnnotations((arr) => arr.map((x) => (x.id === next.id ? next : x)));
  const remove = (annId) => { setAnnotations((arr) => arr.filter((x) => x.id !== annId)); setSelectedId(null); };

  const addSignature = () => {
    if (!hasSignature) {
      toast.error('先に「署名・捺印」設定で署名をアップロードしてください');
      return;
    }
    const a = { id: uid(), type: 'signature', x: 62, y: 70, w: sigDefault.w, h: sigDefault.h, value: '', fontSize: 13, color: '#1f2937', rotation: 0 };
    setAnnotations((arr) => [...arr, a]);
    setSelectedId(a.id);
  };
  const addText = () => {
    const a = { id: uid(), type: 'text', x: 40, y: 45, w: 24, h: 6, value: '承認', fontSize: 2.4, color: '#1f2937', rotation: 0 };
    setAnnotations((arr) => [...arr, a]);
    setSelectedId(a.id);
  };
  const addDate = () => {
    const a = { id: uid(), type: 'date', x: 40, y: 55, w: 24, h: 5, value: todayJP(), fontSize: 2.0, color: '#1f2937', rotation: 0 };
    setAnnotations((arr) => [...arr, a]);
    setSelectedId(a.id);
  };

  const save = async () => {
    const myStage = pickActiveStage(user, submission, stages, location.search);
    if (!myStage) { toast.error('現在あなたが実行できる操作はありません'); return; }
    try {
      setSaving(true);
      await axios.post(`${API}/forms/submissions/${id}/workflow`, { stage: myStage, annotations });
      toast.success(`${STAGE_ACTION[myStage].label}を保存しました`);
      navigate(`/submission/${id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || '保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const removeMyVerification = async () => {
    const myStage = pickActiveStage(user, submission, stages, location.search);
    const existing = myStage && (submission.verifications || []).find((v) => v.stage === myStage);
    if (!existing) { setAnnotations([]); setSelectedId(null); return; }
    try {
      await axios.delete(`${API}/forms/submissions/${id}/workflow/${myStage}`);
      toast.success(`${STAGE_ACTION[myStage].label}を取り消しました`);
      navigate(`/submission/${id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || '取り消しに失敗しました');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-[400px]"><p className="text-zinc-500">読み込み中...</p></div>;
  }
  if (!submission) {
    return <div className="flex items-center justify-center min-h-[400px]"><p className="text-zinc-500">申請が見つかりません</p></div>;
  }

  const selected = annotations.find((a) => a.id === selectedId);
  const myStage = pickActiveStage(user, submission, stages, location.search);
  const otherVerifiers = (submission.verifications || []).filter((v) => v.stage !== myStage);
  const stageKey = computeStage(submission, stages);
  const badge = STAGE_BADGE[stageKey] || {};
  const myExisting = myStage && (submission.verifications || []).find((v) => v.stage === myStage);

  return (
    <div data-testid="verify-page">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => navigate(`/submission/${id}`)} data-testid="verify-back" className="border-zinc-300">
            <ArrowLeft size={18} className="mr-2" /> 戻る
          </Button>
          <span data-testid="verify-stage-badge" className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${badge.cls || 'bg-zinc-100 text-zinc-600 border-zinc-200'}`}>
            {badge.label || '—'}
          </span>
          <span className="hidden sm:flex items-center gap-1 text-[11px] text-zinc-400">
            {stages.map((s, i) => (
              <span key={s} className={`${(submission.verifications || []).some((v) => v.stage === s) ? 'text-emerald-600 font-semibold' : myStage === s ? 'text-teal-700 font-semibold' : ''}`}>
                {i > 0 ? '→ ' : ''}{STAGE_ACTION[s].label}
              </span>
            ))}
          </span>
        </div>
        {myStage && (
          <div className="flex items-center gap-2">
            {(myExisting || annotations.length > 0) && (
              <Button variant="outline" onClick={removeMyVerification} data-testid="verify-remove" className="text-red-600 border-red-200 hover:bg-red-50">
                <Trash size={16} className="mr-2" /> 取り消す
              </Button>
            )}
            <Button onClick={save} disabled={saving} data-testid="verify-save" className="bg-teal-700 hover:bg-teal-800">
              <FloppyDisk size={16} className="mr-2" /> {saving ? '保存中...' : `${STAGE_ACTION[myStage].verb}`}
            </Button>
          </div>
        )}
      </div>

      {!myStage && (
        <div data-testid="verify-readonly-banner" className="mb-4 flex items-center gap-2 bg-zinc-50 border border-zinc-200 rounded-lg p-3 text-sm text-zinc-600">
          <Info size={16} className="text-zinc-400" />
          {stageKey === 'approved'
            ? 'この申請はすべての承認が完了しています（閲覧のみ）。'
            : '現在あなたが実行できる操作はありません（前の段階の完了待ち、または権限がありません）。閲覧のみ可能です。'}
        </div>
      )}

      {myStage && stages.slice(stages.indexOf(myStage) + 1).some((s) => (submission.verifications || []).some((v) => v.stage === s)) && (
        <div data-testid="verify-cascade-warning" className="mb-4 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
          <Info size={16} className="text-amber-500 mt-0.5 shrink-0" />
          <span>この「{STAGE_ACTION[myStage].label}」を保存すると、以降に完了している段階（確認・承認）はやり直しになります。</span>
        </div>
      )}

      {/* Toolbar */}
      {myStage && (
      <div className="mb-4 flex flex-wrap items-center gap-2 bg-white border border-zinc-200 rounded-lg p-2.5">
        <span className="text-xs font-semibold text-zinc-500 px-1">{STAGE_ACTION[myStage].label}の項目を追加:</span>
        <Button size="sm" variant="outline" onClick={addSignature} data-testid="add-signature" className="border-zinc-300">
          <Signature size={15} className="mr-1.5" /> 署名・印
        </Button>
        <Button size="sm" variant="outline" onClick={addText} data-testid="add-text" className="border-zinc-300">
          <TextT size={15} className="mr-1.5" /> テキスト
        </Button>
        <Button size="sm" variant="outline" onClick={addDate} data-testid="add-date" className="border-zinc-300">
          <CalendarBlank size={15} className="mr-1.5" /> 日付
        </Button>
        {!hasSignature && (
          <span className="text-[11px] text-amber-600 flex items-center gap-1 ml-1">
            <Info size={13} /> 署名は「署名・捺印」設定で登録してください
          </span>
        )}
      </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-4">
        {/* Sheet + editable overlay */}
        <div className="bg-zinc-100 rounded-lg p-4 overflow-auto" onPointerDown={() => setSelectedId(null)}>
          <div ref={containerRef} className={`relative inline-block bg-white shadow-md ${PAGE_CLASS[submission.form_type] || ''}`} style={{ verticalAlign: 'top' }}>
            {renderFormSheet(submission)}
            {/* other verifiers (read-only) */}
            <VerificationOverlay verifications={otherVerifiers} />
            {/* my editable annotations */}
            <div style={{ position: 'absolute', inset: 0, containerType: 'inline-size', pointerEvents: 'none', zIndex: 10 }}>
              {annotations.map((a) => (
                <DraggableAnnotation
                  key={a.id}
                  a={a}
                  containerRef={containerRef}
                  selected={selectedId === a.id}
                  onSelect={setSelectedId}
                  onChange={update}
                  onDelete={remove}
                  signatureSrc={`${API}/signatures/user/${user.id}`}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Inspector */}
        <div className="space-y-4">
          <div className="bg-white border border-zinc-200 rounded-lg p-4">
            <h3 className="text-sm font-bold text-zinc-900 mb-3">選択中の項目</h3>
            {!selected ? (
              <p className="text-xs text-zinc-400">配置した項目をクリックすると、ここで編集・サイズ調整できます。ドラッグで移動、右下のハンドルでサイズ変更します。</p>
            ) : (
              <div className="space-y-3" data-testid="inspector">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-zinc-400">
                  {selected.type === 'signature' ? '署名・印' : selected.type === 'date' ? '日付' : 'テキスト'}
                </p>
                {selected.type !== 'signature' && (
                  <div>
                    <label className="text-xs text-zinc-600">内容</label>
                    <input
                      type="text"
                      value={selected.value}
                      onChange={(e) => update({ ...selected, value: e.target.value })}
                      data-testid="inspector-value"
                      className="mt-1 w-full px-2.5 py-1.5 text-sm border border-zinc-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-200"
                    />
                  </div>
                )}
                {selected.type !== 'signature' && (
                  <div>
                    <label className="text-xs text-zinc-600">文字サイズ: {selected.fontSize.toFixed(1)}</label>
                    <input
                      type="range" min="1" max="8" step="0.1" value={selected.fontSize}
                      onChange={(e) => update({ ...selected, fontSize: parseFloat(e.target.value) })}
                      data-testid="inspector-fontsize"
                      className="mt-1 w-full"
                    />
                  </div>
                )}
                {selected.type !== 'signature' && (
                  <div>
                    <label className="text-xs text-zinc-600">色</label>
                    <input
                      type="color" value={selected.color}
                      onChange={(e) => update({ ...selected, color: e.target.value })}
                      data-testid="inspector-color"
                      className="mt-1 h-8 w-full rounded-md border border-zinc-300"
                    />
                  </div>
                )}
                <div>
                  <label className="text-xs text-zinc-600">回転: {Math.round(selected.rotation)}°</label>
                  <input
                    type="range" min="-45" max="45" step="1" value={selected.rotation}
                    onChange={(e) => update({ ...selected, rotation: parseFloat(e.target.value) })}
                    className="mt-1 w-full"
                  />
                </div>
                <Button size="sm" variant="outline" onClick={() => remove(selected.id)} className="w-full text-red-600 border-red-200 hover:bg-red-50">
                  <Trash size={14} className="mr-1.5" /> この項目を削除
                </Button>
              </div>
            )}
          </div>

          {otherVerifiers.length > 0 && (
            <div className="bg-white border border-zinc-200 rounded-lg p-4">
              <h3 className="text-sm font-bold text-zinc-900 mb-2">他の検証者</h3>
              <ul className="space-y-1.5">
                {otherVerifiers.map((v) => (
                  <li key={v.id} className="text-xs text-zinc-600 flex items-center justify-between">
                    <span className="truncate"><span data-no-translate>{v.user_name}</span></span>
                    <span className="text-zinc-400 shrink-0 ml-2">{formatDate(v.verified_at)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
